/**
 * Rain-occlusion volumes (runtime-handoff-v5, directive §2).
 *
 * The primary rain-occlusion source is data/rain_occlusion_volumes.json — a set of
 * world-space AABBs (metres, Y-up). `stadium_weather_occlusion.glb` is the
 * intentionally-empty 148-byte file and must NOT be loaded as the primary source.
 *
 * Rain and splash particles must be suppressed inside these volumes: the tunnel,
 * both dugouts, and the roof-covered / concourse volumes.
 */
import { Box3, Vector3 } from 'three';

export interface RainOcclusionVolume {
  name: string;
  box: Box3;
}

/** Parse the AABB volumes. Throws (fail loud) if a required protected volume is
 *  absent, so rain can never silently fall in the tunnel/dugouts. */
export function parseRainOcclusion(json: unknown): RainOcclusionVolume[] {
  const root = json as { volumes?: Record<string, { min?: number[]; max?: number[] }> };
  const volumes: RainOcclusionVolume[] = [];
  for (const [name, v] of Object.entries(root.volumes ?? {})) {
    const min = v.min ?? [];
    const max = v.max ?? [];
    if (min.length !== 3 || max.length !== 3 || [...min, ...max].some((n) => !Number.isFinite(n))) {
      throw new Error(`rainOcclusion: volume "${name}" has a malformed AABB`);
    }
    volumes.push({ name, box: new Box3(new Vector3(min[0], min[1], min[2]), new Vector3(max[0], max[1], max[2])) });
  }
  for (const need of ['Tunnel', 'DugoutHome', 'DugoutAway']) {
    if (!volumes.some((vv) => vv.name.includes(need))) {
      throw new Error(`rainOcclusion: missing a required protected volume containing "${need}"`);
    }
  }
  return volumes;
}

/** True if a world point is inside ANY occlusion volume — i.e. rain is suppressed
 *  there. Hot path (per particle): a plain AABB test, no allocation. */
export function isOccluded(volumes: readonly RainOcclusionVolume[], x: number, y: number, z: number): boolean {
  for (const v of volumes) {
    const b = v.box;
    if (
      x >= b.min.x && x <= b.max.x &&
      y >= b.min.y && y <= b.max.y &&
      z >= b.min.z && z <= b.max.z
    ) return true;
  }
  return false;
}
